import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Table from '../common/Table';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/admin.css';

const EMPTY = {
  enabled: false,
  require_payment: true,
  environment: 'sandbox',
  consumer_key: '',
  consumer_secret: '',
  passkey: '',
  shortcode: '',
  paybill_number: '',
  account_prefix: 'HVQ',
  stk_callback_url: '',
  c2b_confirmation_url: '',
  c2b_validation_url: '',
  transaction_type: 'CustomerPayBillOnline'
};

const MpesaBilling = () => {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({ configured: false, consumer_secret_set: false, passkey_set: false });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, payRes] = await Promise.all([
        api.get('/admin/mpesa'),
        api.get('/admin/subscription-payments', { params: { limit: 30 } })
      ]);
      const c = cfgRes.data.config || {};
      setForm({
        enabled: !!c.enabled,
        require_payment: c.require_payment !== false,
        environment: c.environment || 'sandbox',
        consumer_key: c.consumer_key || '',
        consumer_secret: '',
        passkey: '',
        shortcode: c.shortcode || '',
        paybill_number: c.paybill_number || '',
        account_prefix: c.account_prefix || 'HVQ',
        stk_callback_url: c.stk_callback_url || '',
        c2b_confirmation_url: c.c2b_confirmation_url || '',
        c2b_validation_url: c.c2b_validation_url || '',
        transaction_type: c.transaction_type || 'CustomerPayBillOnline'
      });
      setMeta({
        configured: !!c.configured,
        consumer_secret_set: !!c.consumer_secret_set,
        passkey_set: !!c.passkey_set
      });
      setPayments(payRes.data.payments || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load M-Pesa settings');
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.consumer_secret) delete payload.consumer_secret;
      if (!payload.passkey) delete payload.passkey;
      const res = await api.put('/admin/mpesa', payload);
      toast.success(res.data.message || 'Saved');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestStk = async () => {
    if (!testPhone) {
      toast.error('Enter a phone number');
      return;
    }
    setTesting(true);
    try {
      const res = await api.post('/admin/mpesa/test-stk', { phone: testPhone });
      toast.success(res.data.message || 'STK sent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test STK failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading M-Pesa billing…</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>M-Pesa Paybill Billing</h1>
          <p>
            Platform Paybill for tenant app subscriptions. Tenants pay via STK Push or Lipa na M-Pesa Paybill;
            access unlocks only after a successful transaction.
          </p>
        </div>
        <span className={`status-pill ${meta.configured && form.enabled ? 'active' : 'inactive'}`}>
          {meta.configured && form.enabled ? 'Live ready' : 'Not active'}
        </span>
      </div>

      <form onSubmit={handleSave} className="admin-two-col" style={{ alignItems: 'start' }}>
        <Card title="Daraja / Paybill credentials">
          <label className="form-check">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setField('enabled', e.target.checked)}
            />
            Enable platform M-Pesa billing
          </label>
          <label className="form-check">
            <input
              type="checkbox"
              checked={form.require_payment}
              onChange={(e) => setField('require_payment', e.target.checked)}
            />
            Require payment before unlocking apps
          </label>

          <div className="form-group">
            <label>Environment</label>
            <select
              value={form.environment}
              onChange={(e) => setField('environment', e.target.value)}
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>

          <Input
            label="Consumer Key"
            value={form.consumer_key}
            onChange={(e) => setField('consumer_key', e.target.value)}
          />
          <Input
            label={`Consumer Secret${meta.consumer_secret_set ? ' (leave blank to keep)' : ''}`}
            type="password"
            value={form.consumer_secret}
            onChange={(e) => setField('consumer_secret', e.target.value)}
            placeholder={meta.consumer_secret_set ? '••••••••' : ''}
          />
          <Input
            label={`Passkey${meta.passkey_set ? ' (leave blank to keep)' : ''}`}
            type="password"
            value={form.passkey}
            onChange={(e) => setField('passkey', e.target.value)}
            placeholder={meta.passkey_set ? '••••••••' : ''}
          />
          <Input
            label="Business Shortcode (STK)"
            value={form.shortcode}
            onChange={(e) => setField('shortcode', e.target.value)}
            placeholder="e.g. 174379"
          />
          <Input
            label="Paybill Number (shown to tenants)"
            value={form.paybill_number}
            onChange={(e) => setField('paybill_number', e.target.value)}
            placeholder="Same as shortcode for most Paybills"
          />
          <Input
            label="Account reference prefix"
            value={form.account_prefix}
            onChange={(e) => setField('account_prefix', e.target.value)}
            placeholder="HVQ"
          />
          <p className="help-text">
            Tenant account number = prefix + business ID (example: HVQ12). Customers enter this as Bill Reference.
          </p>

          <Button type="submit" variant="primary" loading={saving}>
            Save M-Pesa settings
          </Button>
        </Card>

        <div>
          <Card title="Callback URLs (register in Daraja)">
            <Input
              label="STK Callback URL"
              value={form.stk_callback_url}
              onChange={(e) => setField('stk_callback_url', e.target.value)}
            />
            <Input
              label="C2B Validation URL"
              value={form.c2b_validation_url}
              onChange={(e) => setField('c2b_validation_url', e.target.value)}
            />
            <Input
              label="C2B Confirmation URL"
              value={form.c2b_confirmation_url}
              onChange={(e) => setField('c2b_confirmation_url', e.target.value)}
            />
            <p className="help-text">
              Register these URLs in the Safaricom Daraja portal (STK + C2B). Confirmation unlocks apps when
              tenants pay via Paybill manually.
            </p>
          </Card>

          <Card title="Test STK Push (KES 1)">
            <Input
              label="Phone (07… or 2547…)"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
            />
            <Button type="button" variant="secondary" loading={testing} onClick={handleTestStk}>
              Send test prompt
            </Button>
          </Card>
        </div>
      </form>

      <Card title="Recent subscription payments" className="mt-3">
        <Table
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'business_name', label: 'Business' },
            {
              key: 'amount',
              label: 'Amount',
              render: (value) => formatCurrency(value)
            },
            { key: 'account_reference', label: 'Account' },
            { key: 'payment_method', label: 'Method' },
            { key: 'status', label: 'Status' },
            { key: 'mpesa_receipt', label: 'Receipt' },
            {
              key: 'created_at',
              label: 'Created',
              render: (value) => formatDate(value)
            }
          ]}
          data={payments}
          emptyMessage="No subscription payments yet"
        />
      </Card>
    </div>
  );
};

export default MpesaBilling;
